'use client';

import { useEffect } from 'react';

/**
 * Restores a pending caret offset after the value re-render has landed, then
 * clears it. Keyed on the caret so it fires once per insertion.
 *
 * The deferral is the point: setting the selection synchronously alongside the
 * value update gets clobbered by the re-render and drops the caret to the end
 * of the field.
 */
export function useCaretRestore(
  caret: number | null,
  setCaret: React.Dispatch<React.SetStateAction<number | null>>,
  ref: React.RefObject<HTMLTextAreaElement>,
) {
  useEffect(() => {
    if (caret === null) return;
    const el = ref.current;
    if (!el) {
      setCaret(null);
      return;
    }
    const frame = requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(caret, caret);
      setCaret(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [caret, setCaret, ref]);
}
